// src/api/fuenteTasa.js - API para manejo de fuentes de tasas

import { apiFetch } from './index.js';

/**
 * Obtener todas las fuentes de tasa
 * @param {boolean} soloActivas - Si solo se deben obtener las activas
 * @returns {Promise} Lista de fuentes
 */
export const obtenerFuentes = async (soloActivas = false) => {
  try {
    console.log('📋 Obteniendo fuentes de tasa...', { soloActivas });
    
    const endpoint = soloActivas ? '/FuenteTasa/activas' : '/FuenteTasa';
    const response = await apiFetch(endpoint);
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Fuentes obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo fuentes:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener una fuente por ID
 * @param {number} id - ID de la fuente
 * @returns {Promise} Datos de la fuente
 */
export const obtenerFuentePorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de fuente es requerido');
    }
    
    console.log(`🔍 Obteniendo fuente ID: ${id}`);
    
    const response = await apiFetch(`/FuenteTasa/${id}`);
    
    return {
      success: true,
      data: response,
      message: 'Fuente obtenida exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo fuente ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener fuente con todas sus relaciones
 * @param {number} id - ID de la fuente
 * @returns {Promise} Fuente con relaciones
 */
export const obtenerFuenteConRelaciones = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de fuente es requerido');
    }
    
    console.log(`🔍 Obteniendo fuente con relaciones ID: ${id}`);
    
    const response = await apiFetch(`/FuenteTasa/${id}/conRelaciones`);
    
    return {
      success: true,
      data: response,
      message: 'Fuente con relaciones obtenida exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo fuente con relaciones ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Verificar si existe una fuente por ID
 * @param {number} id - ID de la fuente
 * @returns {Promise} Resultado de la verificación
 */
export const verificarFuenteExiste = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de fuente es requerido');
    }
    
    console.log(`🔎 Verificando existencia de fuente ID: ${id}`);
    
    const response = await apiFetch(`/FuenteTasa/${id}/exists`);
    
    return {
      success: true,
      existe: response?.existe || false,
      data: response,
      message: 'Verificación completada'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando fuente ${id}:`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Crear una nueva fuente
 * @param {Object} fuenteData - Datos de la fuente
 * @returns {Promise} Fuente creada
 */
export const crearFuente = async (fuenteData) => {
  try {
    // Validaciones
    if (!fuenteData.nombre || fuenteData.nombre.trim() === '') {
      throw new Error('El nombre de la fuente es requerido');
    }
    
    console.log('➕ Creando nueva fuente:', fuenteData);
    
    const response = await apiFetch('/FuenteTasa', {
      method: 'POST',
      body: JSON.stringify({
        nombre: fuenteData.nombre.trim(),
        descripcion: fuenteData.descripcion || null,
        proposito: fuenteData.proposito || null,
        es_activa: fuenteData.es_activa !== undefined ? fuenteData.es_activa : true
      })
    });
    
    return {
      success: true,
      data: response,
      message: 'Fuente creada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando fuente:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar una fuente existente
 * @param {number} id - ID de la fuente
 * @param {Object} fuenteData - Datos a actualizar
 * @returns {Promise} Fuente actualizada
 */
export const actualizarFuente = async (id, fuenteData) => {
  try {
    if (!id) {
      throw new Error('ID de fuente es requerido');
    }
    
    if (!fuenteData.nombre || fuenteData.nombre.trim() === '') {
      throw new Error('El nombre de la fuente es requerido');
    }
    
    console.log(`✏️ Actualizando fuente ID: ${id}`, fuenteData);
    
    const updateData = {
      nombre: fuenteData.nombre.trim(),
      descripcion: fuenteData.descripcion || null,
      proposito: fuenteData.proposito || null,
      es_activa: fuenteData.es_activa !== undefined ? fuenteData.es_activa : true
    };
    
    const response = await apiFetch(`/FuenteTasa/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
    
    return {
      success: true,
      data: response,
      message: 'Fuente actualizada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando fuente ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Eliminar una fuente
 * @param {number} id - ID de la fuente a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarFuente = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de fuente es requerido');
    }
    
    console.log(`🗑️ Eliminando fuente ID: ${id}`);
    
    const response = await apiFetch(`/FuenteTasa/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response,
      message: 'Fuente eliminada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando fuente ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Cambiar estado de una fuente
 * @param {number} id - ID de la fuente
 * @param {boolean} activa - Nuevo estado activo/inactivo
 * @returns {Promise} Resultado del cambio de estado
 */
export const cambiarEstadoFuente = async (id, activa) => {
  try {
    if (!id || activa === undefined) {
      throw new Error('ID y estado son requeridos');
    }
    
    console.log(`🔄 Cambiando estado de fuente ID: ${id} a ${activa ? 'activa' : 'inactiva'}`);
    
    // No hay un endpoint específico PATCH, así que obtenemos y actualizamos
    const fuenteActual = await obtenerFuentePorId(id);
    
    if (!fuenteActual.success) {
      throw new Error('No se pudo obtener la fuente actual');
    }
    
    return await actualizarFuente(id, {
      ...fuenteActual.data,
      es_activa: activa
    });
    
  } catch (error) {
    console.error(`❌ Error cambiando estado de fuente ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Activar una fuente
 * @param {number} id - ID de la fuente
 * @returns {Promise} Resultado de la activación
 */
export const activarFuente = async (id) => {
  return await cambiarEstadoFuente(id, true);
};

/**
 * Desactivar una fuente
 * @param {number} id - ID de la fuente
 * @returns {Promise} Resultado de la desactivación
 */
export const desactivarFuente = async (id) => {
  return await cambiarEstadoFuente(id, false);
};

/**
 * Obtener fuentes por propósito
 * @param {string} proposito - Propósito de la fuente (compra/venta)
 * @returns {Promise} Fuentes filtradas por propósito
 */
export const obtenerFuentesPorProposito = async (proposito) => {
  try {
    if (!proposito) {
      throw new Error('Propósito es requerido');
    }
    
    console.log(`🎯 Obteniendo fuentes con propósito: ${proposito}`);
    
    const response = await obtenerFuentes(true);
    
    if (!response.success) {
      return response;
    }
    
    const filtradas = response.data.filter(f => 
      f.proposito?.toLowerCase() === proposito.toLowerCase() && f.es_activa === true
    );
    
    return {
      success: true,
      data: filtradas,
      count: filtradas.length,
      message: 'Fuentes filtradas por propósito obtenidas'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo fuentes por propósito ${proposito}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

// ==================== FUNCIONES AUXILIARES ====================

/**
 * Formatear nombre de fuente
 * @param {string} nombre - Nombre de la fuente
 * @returns {string} Nombre formateado
 */
export const formatearNombreFuente = (nombre) => {
  if (!nombre) return 'N/A';
  return nombre.toUpperCase();
};

/**
 * Obtener color de badge para fuente
 * @param {string} nombre - Nombre de la fuente
 * @returns {string} Clase de color
 */
export const obtenerColorFuente = (nombre) => {
  if (!nombre) return 'bg-gray-500';
  
  const colores = {
    'BCV': 'bg-blue-600',
    'USDT': 'bg-green-600',
    'PARALELO': 'bg-purple-600',
    'DÓLAR TODAY': 'bg-orange-600',
    'ENPARALELOVZLA': 'bg-pink-600'
  };
  
  const nombreUpper = nombre.toUpperCase();
  return colores[nombreUpper] || 'bg-indigo-600';
};

/**
 * Buscar fuentes por término
 * @param {Array} fuentes - Lista de fuentes
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Fuentes filtradas
 */
export const buscarFuentes = (fuentes, termino) => {
  if (!termino || termino.trim() === '') {
    return fuentes;
  }
  
  const terminoLower = termino.toLowerCase();
  return fuentes.filter(f => 
    (f.nombre || '').toLowerCase().includes(terminoLower) ||
    (f.descripcion || '').toLowerCase().includes(terminoLower) ||
    (f.proposito || '').toLowerCase().includes(terminoLower)
  );
};

export default {
  obtenerFuentes,
  obtenerFuentePorId,
  obtenerFuenteConRelaciones,
  verificarFuenteExiste,
  crearFuente,
  actualizarFuente,
  eliminarFuente,
  activarFuente,
  desactivarFuente,
  cambiarEstadoFuente,
  obtenerFuentesPorProposito,
  formatearNombreFuente,
  obtenerColorFuente,
  buscarFuentes
};